import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Shield, User, Users } from "lucide-react";

export default function UsersManagement() {
  const { toast } = useToast();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAdminAndUser = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const { data: role, error: roleError } = await supabase.rpc('get_current_user_role');
          
          if (roleError) {
            console.error("Error getting user role:", roleError);
            return;
          }
          
          const userIsAdmin = role === 'admin';
          setIsAdmin(userIsAdmin);
          
          if (!userIsAdmin) {
            toast({
              variant: "destructive",
              title: "Acesso negado",
              description: "Você não tem permissão para acessar esta página.",
            });
          }
        }
      } catch (error) {
        console.error("Error checking admin:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAdminAndUser();
  }, [toast]);

  const { data: profiles, error } = useQuery({
    queryKey: ["all-profiles"],
    queryFn: async () => {
      if (!isAdmin) return [];
      
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .order("created_at", { ascending: false });
          
        if (error) throw error;
        
        return data || [];
      } catch (error) {
        console.error("Error fetching profiles:", error);
        toast({
          variant: "destructive",
          title: "Erro ao carregar usuários",
          description: "Por favor, tente novamente mais tarde.",
        });
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    enabled: isAdmin,
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR');
  };

  if (!isAdmin) {
    return (
      <Card className="p-10 text-center">
        <div className="mx-auto mb-4 w-16 h-16 bg-red-50 rounded-full flex items-center justify-center">
          <Shield className="h-8 w-8 text-red-500" />
        </div>
        <h3 className="text-lg font-semibold mb-2">Acesso negado</h3>
        <p className="text-gray-500 max-w-md mx-auto">
          Você não tem permissão para acessar esta página. Esta funcionalidade é restrita a administradores.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-8 rounded-xl text-white shadow-lg mb-8">
        <h1 className="text-3xl font-bold mb-2">Gerenciamento de Usuários</h1>
        <p className="text-blue-50">Gerencie os usuários registrados na plataforma</p>
      </div>

      {isLoading ? (
        <Card className="p-8">
          <div className="flex justify-center">
            <div className="animate-pulse flex space-x-4">
              <div className="rounded-full bg-slate-200 h-10 w-10"></div>
              <div className="flex-1 space-y-4 py-1">
                <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                <div className="space-y-2">
                  <div className="h-4 bg-slate-200 rounded"></div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      ) : error ? (
        <Card className="p-10 text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-red-50 rounded-full flex items-center justify-center">
            <Users className="h-8 w-8 text-red-500" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Erro ao carregar usuários</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            Houve um problema ao carregar os usuários. Por favor, tente novamente.
          </p>
        </Card>
      ) : profiles && profiles.length > 0 ? (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead className="font-semibold">Usuário</TableHead>
                <TableHead className="font-semibold">WhatsApp</TableHead>
                <TableHead className="font-semibold">Criado em</TableHead>
                <TableHead className="font-semibold">Função</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {profiles.map((profile) => (
                <TableRow key={profile.id} className="hover:bg-gray-50 transition-colors">
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-500" />
                      {profile.email}
                    </div>
                  </TableCell>
                  <TableCell>
                    {profile.whatsapp || 'Não informado'}
                  </TableCell>
                  <TableCell>
                    {formatDate(profile.created_at)}
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      profile.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {profile.role === 'admin' ? 'Administrador' : 'Usuário'}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card className="p-10 text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center">
            <Users className="h-8 w-8 text-blue-500" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Nenhum usuário encontrado</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            Ainda não há usuários registrados na plataforma.
          </p>
        </Card>
      )}
    </div>
  );
}